import {
  ArrowUpRight,
  CalendarDays,
  Download,
  Gauge,
  Search,
  ShieldAlert,
  TrendingUp,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Pagination } from "../components/Pagination";
import type { PageId } from "../components/Shell";
import type { DecisionPlan, ReplenishmentAction, VelocityClass } from "../types";

interface ReplenishmentPageProps {
  plan: DecisionPlan;
  onNavigate: (page: PageId) => void;
}

const filters: Array<"全部" | VelocityClass> = ["全部", "爆旺", "平销", "平滞"];
const ORDERS_PER_PAGE = 20;

function downloadCsv(rows: ReplenishmentAction[], asOf: string) {
  const headers = [
    "SKU",
    "商品",
    "热度",
    "预测日均",
    "预测下限",
    "预测上限",
    "补货周期",
    "周期天数",
    "周期信心",
    "异常脉冲日",
    "建议补货",
    "现有库存天数",
    "建议下单日",
    "置信度",
    "依据",
  ];
  const escape = (value: string | number) => `"${String(value).replaceAll('"', '""')}"`;
  const content = `\uFEFF${[
    headers.join(","),
    ...rows.map((row) =>
      [
        row.sku,
        row.product,
        row.category,
        row.forecastDaily.toFixed(2),
        row.forecastLowerDaily.toFixed(2),
        row.forecastUpperDaily.toFixed(2),
        row.cycle,
        row.periodDays || "未检出",
        `${(row.periodConfidence * 100).toFixed(0)}%`,
        row.shockDays.join(" / ") || "无",
        row.suggestedQuantity,
        row.stockDays.toFixed(1),
        row.nextOrderDate,
        row.confidence,
        row.reason,
      ].map(escape).join(","),
    ),
  ].join("\n")}`;
  const url = URL.createObjectURL(new Blob([content], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `仓序-补货方案-${asOf}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function cycleLabel(cycle: number) {
  return cycle === 1 ? "每日滚动" : cycle === 7 ? "7 日波次" : "30 日集中";
}

export function ReplenishmentPage({ plan, onNavigate }: ReplenishmentPageProps) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<(typeof filters)[number]>("全部");
  const [page, setPage] = useState(1);
  const [selectedSku, setSelectedSku] = useState(plan.replenishments[0]?.sku ?? "");

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return plan.replenishments.filter(
      (item) =>
        (filter === "全部" || item.category === filter) &&
        (!needle || item.product.toLowerCase().includes(needle) || item.sku.toLowerCase().includes(needle)),
    );
  }, [filter, plan.replenishments, query]);

  useEffect(() => setPage(1), [query, filter, plan.generatedAt]);

  const paged = filtered.slice((page - 1) * ORDERS_PER_PAGE, page * ORDERS_PER_PAGE);
  const selected =
    paged.find((item) => item.sku === selectedSku) ?? paged[0] ?? filtered[0] ?? plan.replenishments[0];
  const urgent = plan.replenishments.filter((item) => item.stockDays <= item.cycle);
  const totalUnits = plan.replenishments.reduce((sum, item) => sum + item.suggestedQuantity, 0);
  const dailyCount = plan.replenishments.filter((item) => item.cycle === 1).length;
  const groupedCycle = [1, 7, 30].map((cycle) => ({
    cycle,
    items: plan.replenishments.filter((item) => item.cycle === cycle),
  }));

  return (
    <div className="replenishment-page">
      <section className="analysis-heading replenishment-heading">
        <div>
          <span className="section-kicker">REPLENISHMENT RHYTHM</span>
          <h2>补货不是填库存，<br />是让节奏匹配需求。</h2>
        </div>
        <p>
          补货量取阻尼 Holt、周期项与稳健残差形成的 90% 需求区间上界，再扣除现有与在途库存；
          单日异常会作为脉冲降权处理，低置信品项仍需人工复核。
        </p>
      </section>

      <section className="replenishment-hero">
        <div className="replenishment-pulse">
          <span className="section-kicker">ROLLING WINDOW</span>
          <div className="pulse-orbit" aria-hidden="true">
            <span>{urgent.length.toString().padStart(2, "0")}</span>
            <i />
            <i />
            <i />
          </div>
          <h3>个品项已进入补货窗口</h3>
          <p>按当前库存、在途库存与预测需求区间计算，先处理库存无法覆盖补货周期的高风险品项。</p>
        </div>
        <div className="replenishment-kpis">
          <div><span><Gauge size={17} />建议补货总量</span><strong>{totalUnits.toLocaleString("zh-CN")}</strong><small>件 · 本批次合计</small></div>
          <div><span><TrendingUp size={17} />每日滚动品项</span><strong>{dailyCount}</strong><small>爆旺需求，优先保持现货</small></div>
          <div><span><ShieldAlert size={17} />异常脉冲品项</span><strong>{plan.replenishments.filter((item) => item.shockDays.length > 0).length}</strong><small>已稳健降权，未使用简单均值兜底</small></div>
        </div>
        <div className="cycle-spectrum">
          <span className="section-kicker">CYCLE SPECTRUM</span>
          {groupedCycle.map((group) => (
            <div key={group.cycle}>
              <span>{cycleLabel(group.cycle)}</span>
              <strong>{group.items.length}</strong>
              <small>{group.items.reduce((sum, item) => sum + item.suggestedQuantity, 0).toLocaleString("zh-CN")} 件</small>
            </div>
          ))}
        </div>
      </section>

      <section className="replenishment-workbench">
        <div className="directory-toolbar replenishment-toolbar">
          <div>
            <span className="section-kicker">ORDER PROPOSALS</span>
            <h3>可执行的补货清单</h3>
          </div>
          <label className="search-box">
            <Search size={16} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索 SKU 或品项" />
          </label>
          <div className="segmented-control" role="group" aria-label="补货热度筛选">
            {filters.map((item) => (
              <button key={item} className={filter === item ? "is-active" : ""} onClick={() => setFilter(item)}>
                {item}
              </button>
            ))}
          </div>
          <button className="secondary-button" onClick={() => downloadCsv(filtered, plan.generatedAt.slice(0, 10))}>
            <Download size={16} /> 导出清单
          </button>
        </div>

        <div className="replenishment-layout">
          <div className="replenishment-list">
            <div className="replenishment-row replenishment-row-head">
              <span>品项</span><span>热度</span><span>预测日均</span><span>周期</span><span>建议量</span><span>库存天数</span><span>下单日</span>
            </div>
            {paged.map((item) => (
              <button
                key={item.sku}
                className={`replenishment-row ${selected?.sku === item.sku ? "is-selected" : ""}`}
                onClick={() => setSelectedSku(item.sku)}
              >
                <span className="product-name-cell"><strong>{item.product}</strong><small>{item.sku}</small></span>
                <span><i className={`category-dot ${item.category === "爆旺" ? "hot" : item.category === "平滞" ? "slow" : "stable"}`} />{item.category}</span>
                <span>{item.forecastDaily.toFixed(1)}</span>
                <span>{item.cycle} 日</span>
                <span className="suggested-quantity">{item.suggestedQuantity}</span>
                <span className={item.stockDays <= item.cycle ? "negative" : ""}>{Math.round(item.stockDays)} 天</span>
                <span>{item.nextOrderDate.slice(5).replace("-", ".")}</span>
              </button>
            ))}
            {!filtered.length && <div className="empty-state">当前筛选下没有补货建议。</div>}
          </div>

          {selected && (
            <aside className="replenishment-inspector">
              <div className="panel-head compact-head">
                <div>
                  <span className="section-kicker">ORDER CARD</span>
                  <h3>{selected.product}</h3>
                  <small>{selected.sku}</small>
                </div>
                <span className="confidence-badge">置信度 {selected.confidence}</span>
              </div>
              <div className="order-card-amount">
                <span>建议补货</span>
                <strong>{selected.suggestedQuantity}</strong>
                <small>件</small>
              </div>
              <div className="order-card-meta">
                <div><span><CalendarDays size={15} />建议下单</span><strong>{selected.nextOrderDate}</strong></div>
                <div><span>补货周期</span><strong>{cycleLabel(selected.cycle)}</strong></div>
                <div><span>预测日均</span><strong>{selected.forecastDaily.toFixed(1)} 件</strong></div>
                <div><span>预测 90% 区间</span><strong>{selected.forecastLowerDaily.toFixed(1)}–{selected.forecastUpperDaily.toFixed(1)} 件</strong></div>
                <div><span>需求周期</span><strong>{selected.periodDays ? `${selected.periodDays} 日 · 信心 ${(selected.periodConfidence * 100).toFixed(0)}%` : "未检出"}</strong></div>
                <div><span>现有库存可售</span><strong>{Math.round(selected.stockDays)} 天</strong></div>
              </div>
              <div className="order-reason">
                <span>为什么这样补</span>
                <p>{selected.reason}</p>
              </div>
              <div className="order-formula">
                <span>周期需求预测区间上界 − 现有库存 − 在途库存</span>
              </div>
              <button className="primary-button full" onClick={() => onNavigate("assistant")}>
                汇入完整决策方案 <ArrowUpRight size={16} />
              </button>
            </aside>
          )}
        </div>
        {filtered.length > 0 && (
          <Pagination
            page={page}
            pageSize={ORDERS_PER_PAGE}
            totalItems={filtered.length}
            onPageChange={setPage}
            label="补货清单分页"
          />
        )}
      </section>
    </div>
  );
}
